// store/actions/MedicineActions.js
import {
  CONFIRMED_CREATE_MEDICINE_ACTION,
  CONFIRMED_DELETE_MEDICINE_ACTION,
  CONFIRMED_EDIT_MEDICINE_ACTION,
  CONFIRMED_GET_MEDICINES,
} from "../actions/MedicinesType";

import {
  createMedicine,
  formatMedicines,
  getMedicines,
  deleteMedicine,
  updateMedicine,
} from "../../services/MedicinesService";

import swal from "sweetalert";
import { handleError, handleSuccess, showErrorAlert } from "../../services/ErrorHandlingService";

// Get token from Redux or fallback to localStorage
function getAuthToken(getState) {
  const stateToken = getState().auth?.authKey;
  if (stateToken) return stateToken;

  const storedUser = localStorage.getItem('userDetails');
  return storedUser ? JSON.parse(storedUser).token : null;
}

export function getMedicinesAction(pagination = false, pageid = 0, search_by = "", search_keyword = "", sort_by, sort_order) {
  return async (dispatch, getState) => {
    try {
      const token = getAuthToken(getState);
      const response = await getMedicines(pagination, pageid, search_by, search_keyword, sort_by, sort_order, token);

      if (response && response.status === 200 && response.data) {
        if (response.data.status === "success" && response.data.data) {
          const medicines = formatMedicines(response.data.data);

          dispatch({ type: CONFIRMED_GET_MEDICINES, payload: { medicines: medicines, num_rows: response.data.num_rows } });
        } else {
          handleError("Failed to get medicines: Server error");
        }
      } else {
        handleError("Failed to get medicines: Network error");
      }
    } catch (error) {
      console.error("API Error:", error.response || error);
      showErrorAlert("Failed to get medicines");
    }
  };
}

export function deleteMedicineAction(medicine, history) {
  return async (dispatch) => {
    try {
      await deleteMedicine(medicine);
      dispatch({ type: CONFIRMED_DELETE_MEDICINE_ACTION, payload: { medicine_id: medicine.medicine_id } });
      if (history) history.push("/medicinepage");
      handleSuccess("Medicine deleted successfully!");
    } catch (error) {
      console.error(error);
      showErrorAlert("Failed to delete medicine");
    }
  };
}
// CREATE
export function createMedicineAction(medicineData, history) {
  return async (dispatch) => {
    try {
      const requestPayload = {
        medicine_id: medicineData.medicineId,
        medicine_name: medicineData.medicineName,
        display_name_en: medicineData.displayName,
        display_name_mr: medicineData.displayMarathiName,
        medicine_code: medicineData.medicineCode,
        marathi_corrected_name: medicineData.marathiName,
        medicine_type_id: medicineData.typeId,
        default_dose_id: medicineData.defaultDoseId,
        default_frequency_id: medicineData.defaultFrequencyId,
        default_instructions: medicineData.defaultInstructions,
        how_to_take_ids: medicineData.howToTakeIds.join(","),  // ✅ FIXED
        package_qty: medicineData.packageQty,                  // ✅ FIXED
        unit_of_measure_id: medicineData.unitOfMeasureId,
        value_per_unit: medicineData.valuePerUnit,
        pathyas_id: medicineData.pathyasId,
        // anupan: medicineData.anupan,
        manufacturer: medicineData.manufacturer,
        base_price: medicineData.price,
        CGST: medicineData.cgst,
        SGST: medicineData.sgst,
        IGST: medicineData.igst,
        prescription_required: medicineData.prescriptionRequired,
        cgst_sgst_price: medicineData.finalPricecgstsgst,
        igst_price: medicineData.finalPriceigst,
        medicine_status: medicineData.status,
        minimum_threshold: medicineData.minimum_threshold,
        anupana_id: medicineData.anupanId,
        medicine_int_ext_type: medicineData.isInternal ? "Internal" : "External",
      };

      const response = await createMedicine(requestPayload);

      if (response && response.status === 200) {
        const apiData = response.data;

        const medicine_id = apiData._id || medicineData.medicine_id;
        const singleMedicine = {
          medicine_id,
          ...requestPayload,
        };

        dispatch({
          type: CONFIRMED_CREATE_MEDICINE_ACTION,
          payload: singleMedicine,
        });
        await dispatch(getMedicinesAction());
        if (history) history.push("/medicinepage");
        handleSuccess("Medicine created successfully!");
        return Promise.resolve();
      } else {
        const errorMsg = response?.data?.message || "Failed to create medicine";
        showErrorAlert(errorMsg);
        return Promise.reject(new Error(errorMsg));
      }
    } catch (error) {
      console.error("Create Medicine Error:", error);
      const errorMsg = error.response?.data?.message || "Failed to create medicine";
      showErrorAlert(errorMsg);
      return Promise.reject(error);
    }
  };
}

// UPDATE
export function updateMedicineAction(medicineData, navigate) {
  return async (dispatch) => {
    try {
      const requestPayload = {
        medicine_id: medicineData.id,
        medicine_name: medicineData.medicineName,
        medicine_code: medicineData.medicineCode,
        marathi_corrected_name: medicineData.marathiName,
        display_name_en: medicineData.displayName,
        display_name_mr: medicineData.displayMarathiName,
        medicine_type_id: medicineData.typeId,
        default_dose_id: medicineData.defaultDoseId,
        default_frequency_id: medicineData.defaultFrequencyId,
        default_instructions: medicineData.defaultInstructions,
        how_to_take_ids: medicineData.howToTakeIds.join(","),  // ✅ FIXED
        package_qty: medicineData.packageQty,                  // ✅ FIXED
        unit_of_measure_id: medicineData.unitOfMeasureId,
        value_per_unit: medicineData.valuePerUnit,
        pathyas_id: medicineData.pathyasId,
        anupan: medicineData.anupan,
        manufacturer: medicineData.manufacturer,
        base_price: medicineData.price,
        CGST: medicineData.cgst,
        SGST: medicineData.sgst,
        IGST: medicineData.igst,
        prescription_required: medicineData.prescriptionRequired,
        cgst_sgst_price: medicineData.finalPricecgstsgst,
        igst_price: medicineData.finalPriceigst,
        medicine_status: medicineData.status,
        minimum_threshold: medicineData.minimum_threshold,
        anupana_id: medicineData.anupanId,
        medicine_int_ext_type: medicineData.isInternal ? "Internal" : "External",
      };

      const response = await updateMedicine(requestPayload);

      if (response && response.status === 200 && response.data?.status === "success") {
        const updatedMedicine = response.data.data;
        dispatch({
          type: CONFIRMED_EDIT_MEDICINE_ACTION,
          payload: updatedMedicine,
        });
        handleSuccess("Medicine updated successfully!");
        if (navigate) navigate("/medicine-list");
        return Promise.resolve();
      } else {
        const errorMsg = response?.data?.message || "Failed to update medicine";
        showErrorAlert(errorMsg);
        return Promise.reject(new Error(errorMsg));
      }
    } catch (error) {
      console.error("Update Medicine Error:", error);
      const errorMsg = error.response?.data?.message || "Failed to update medicine";
      showErrorAlert(errorMsg);
      return Promise.reject(error);
    }
  };
}


